import type { ComponentProps, ReactElement } from 'react';
import { Children, cloneElement, Fragment, isValidElement } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant } from './buttonClasses';
import { composeVariant, groupDividerVariantClasses, groupVariantClasses } from './buttonClasses';
import type { Style } from './colors';
import type { Shape, Size } from './types';
import { childRoundedClasses, shapeRounded, sizeHeight } from './types';

type ButtonGroupProps = ComponentProps<'div'> & {
  size?: Size;
  disabled?: boolean;
  shape?: Shape;
  equalWidth?: boolean;
  color?: ColorName;
  tone?: Style;
};

export default function ButtonGroup({ children, className, style, disabled, shape, equalWidth, size = 'md', color, tone }: ButtonGroupProps) {
  const effectiveColor: ColorName = color ?? 'gray';
  const effectiveTone: Style = tone ?? 'filled';
  const effectiveVariant: ColorVariant = composeVariant(effectiveColor, effectiveTone);
  type ChildProps = { size?: Size; shape?: Shape; bordered?: boolean; disabled?: boolean; color?: ColorName; tone?: Style; className?: string };
  const filtered: ReactElement<ChildProps>[] = [];

  // Recursively flatten Fragments and collect valid elements
  const flattenChildren = (kids: typeof children) => {
    Children.forEach(kids, (child) => {
      if (child == null) return;
      if (isValidElement(child)) {
        // Check if it's a Fragment by looking at the type
        if (child.type === Fragment) {
          // Recursively flatten Fragment children
          flattenChildren((child.props as { children?: typeof children }).children);
        } else {
          filtered.push(child as ReactElement<ChildProps>);
        }
      }
    });
  };

  flattenChildren(children);

  if (filtered.length === 0) return null;

  return (
    <div
      style={style}
      className={cn(
        'inline-flex shrink-0 overflow-hidden border',
        groupVariantClasses(effectiveVariant),
        sizeHeight(size),
        shapeRounded(shape, size),
        className,
      )}>
      {filtered.map((child, i) => {
        const isFirst = i === 0;
        const isLast = i === filtered.length - 1;
        return (
          <Fragment key={i}>
            {i > 0 && <div className={cn('h-full w-px', groupDividerVariantClasses(effectiveVariant))} />}
            {cloneElement(child, {
              size,
              shape: 'square',
              bordered: false,
              disabled: child.props.disabled ?? disabled,
              // A child that sets its own color keeps its color+tone; otherwise it inherits the group's.
              ...(child.props.color == null ? { color: effectiveColor, tone: effectiveTone } : {}),
              className: cn(
                'h-full',
                equalWidth && 'flex-1 min-w-0',
                childRoundedClasses(isFirst, isLast, shape),
                child.props.className,
              ),
            })}
          </Fragment>
        );
      })}
    </div>
  );
}
